import { useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { CaseStudyDto } from '@shared/types/CaseStudyDto'

export default function CaseStudyDetailPage() {
  const location = useLocation()
  const study = location.state?.caseStudy as CaseStudyDto | undefined

  if (!study) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6 text-center py-24">
          <h1 className="font-display text-4xl font-bold mb-4">Case Study Not Found</h1>
          <p className="text-muted-foreground mb-8">The case study you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/case-studies">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-24 pb-16">
      <section className="py-8">
        <div className="container mx-auto px-6">
          <Button variant="ghost" asChild className="mb-8">
            <Link to="/case-studies">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline">{study.industry}</Badge>
              <span className="text-muted-foreground">{study.company}</span>
            </div>
            
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
              {study.title}
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl overflow-hidden mb-12"
          >
            <img
              src={study.bannerImageUrl}
              alt={study.title}
              className="w-full h-[400px] object-cover"
            />
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="font-display text-2xl font-bold mb-4">The Challenge</h2>
                <p className="text-muted-foreground leading-relaxed">{study.challenge}</p>
              </motion.div>

              <Separator />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-display text-2xl font-bold mb-4">The Solution</h2>
                <p className="text-muted-foreground leading-relaxed">{study.solution}</p>
              </motion.div>

              <Separator />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h2 className="font-display text-2xl font-bold mb-6">Results</h2>
                <ul className="space-y-3">
                  {study.results.map((result) => (
                    <li key={result} className="flex items-start gap-3">
                      <ArrowRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{result}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Card className="glass-card border-gradient">
                  <CardContent className="p-8">
                    <Quote className="h-8 w-8 text-primary mb-4" />
                    <p className="text-lg italic mb-6">"{study.testimonial.quote}"</p>
                    <div className="flex items-center gap-4">
                      <img
                        src={study.testimonial.avatar}
                        alt={study.testimonial.author}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold">{study.testimonial.author}</div>
                        <div className="text-sm text-muted-foreground">{study.testimonial.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="glass-card border-gradient sticky top-28">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-6">Key Metrics</h3>
                    <div className="space-y-6">
                      {study.metrics.map((metric) => (
                        <div key={metric.label}>
                          <div className="font-display text-3xl font-bold text-primary">
                            {metric.value}{metric.change !== undefined && metric.change !== 0 ? `${metric.change > 0 ? '+' : ''}${metric.change}%` : ''}
                          </div>
                          <div className="text-sm text-muted-foreground">{metric.label}</div>
                        </div>
                      ))}
                    </div>
                    
                    <Separator className="my-6" />
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {study.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                    
                    <Button className="w-full" asChild>
                      <Link to="/discovery">
                        Start Your Project
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

